/* Product page: option pickers, the pack ladder, prices per unit, the free-shipping line and nudge,
   add-ons, the delivery panel (arrival dates, stock, shipping cost), the sticky bar, gallery thumbs + lightbox, size guide, ?variant= deep
   links (ids or 5PACK), ProductSelectEvent, and add to cart through the cart module.
   Until every required option (size, lens power) is chosen the page shows the first available variant of the
   selected pack, keeps the URL clean, holds express checkout and asks for the choice on any buy action. */
import { $, $$, money, fmt, track, layers, getParam, debounce, onReady, focusInto, keyboardUser, unitLabel } from '@theme/utils';
import { ProductModel, readProductJSON } from '@theme/product-data';
import { dispatchProductSelect } from '@theme/events';
import { cart } from '@theme/cart';

const T = () => window.Theme;
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function initPDP(root) {
  const buy = root.querySelector('.buy[data-product-host]'); if (!buy || buy.__pdp) return; buy.__pdp = true;
  const model = readProductJSON(buy); if (!model) return;
  const S = T().strings, SET = T().settings;
  const unitWord = (n) => unitLabel(n, model.unit);
  /* what shipping costs on an order under the free-shipping threshold: the small-order rate under its limit, else the
     rate (theme settings, Commerce, as the server prints it in main-product.liquid); 0 when no rate is set */
  const shipCost = (order) => {
    const small = SET.shippingSmallUnderCents > 0 && SET.shippingRateSmallCents > 0 && order < SET.shippingSmallUnderCents;
    return small ? SET.shippingRateSmallCents : (SET.shippingRateCents || 0);
  };
  const moneyShort = (c) => money(c).replace(/[.,]00(?=\D*$)/, '');
  let sel = model.options.map(() => null);

  /* ---- initial selection: the URL, else what the server rendered ----
     ?variant=<id> names one variant (an ad, a shared link) and is taken whole. ?variant=5PACK names only the pack:
     the pack is set and every other choice stays as on a plain visit, so the size waits for the shopper and no
     size goes in the cart that nobody chose. */
  const variantParam = getParam('variant');
  const fromUrl = model.fromParam(variantParam);
  const packOnly = !!fromUrl && !model.byId(variantParam);
  if (fromUrl && !packOnly) sel = fromUrl.options.slice();
  else {
    $$('input[data-opt]:checked', buy).forEach((i) => { sel[parseInt(i.dataset.opt, 10)] = i.dataset.value || i.value; });
    const packInput = $('input[data-pack]:checked', buy);
    if (packInput && model.packIndex >= 0) sel[model.packIndex] = packInput.dataset.value;
    if (packOnly && model.packIndex >= 0) sel[model.packIndex] = fromUrl.options[model.packIndex];
  }
  model.options.forEach((o, i) => { if (o.values.length === 1) sel[i] = o.values[0]; });
  if (model.colorIndex >= 0 && sel[model.colorIndex] == null) sel[model.colorIndex] = model.options[model.colorIndex].values[0];

  const required = () => model.pickOptions.filter((o) => o.index !== model.colorIndex);
  const missing = () => required().filter((o) => sel[o.index] == null);
  const packSel = () => (model.packIndex >= 0 ? sel[model.packIndex] : null);
  const ladder = () => (model.packIndex >= 0 ? model.ladder(sel) : []);
  const currentRow = () => { const l = ladder(); return l.find((r) => r.variant.options[model.packIndex] === packSel()) || l.find((r) => r.mostChosen) || l[0] || null; };
  const currentVariant = () => { const exact = model.find(sel); if (exact) return exact; const row = currentRow(); return row ? row.variant : model.variants[0]; };
  const qty = () => { const v = currentVariant(); return v ? v.qty : 1; };

  /* ---- add-ons ---- */
  const addonsOn = () => $$('[data-addon-id][aria-checked="true"]', buy).map((b) => ({ id: Number(b.dataset.addonId), price: Number(b.dataset.price), name: b.dataset.name }));
  const addonsOff = () => $$('[data-addon-id][aria-checked="false"]', buy).map((b) => ({ id: Number(b.dataset.addonId), price: Number(b.dataset.price), name: b.dataset.name }));

  /* ---- render ---- */
  function renderOptions() {
    model.pickOptions.forEach((o) => {
      const row = buy.querySelector(`[data-optrow="${o.index}"]`); if (!row) return;
      $$('input[data-opt]', row).forEach((input) => {
        const on = sel[o.index] === input.value;
        input.checked = on;
        const label = input.closest('label'); label.classList.toggle('on', on);
        const probe = sel.slice(); probe[o.index] = input.value; if (model.packIndex >= 0) probe[model.packIndex] = null;
        label.classList.toggle('unavailable', !model.match(probe).some((v) => v.available));
      });
      if (sel[o.index] != null) row.classList.remove('err');
      const name = row.querySelector('[data-colorname]'); if (name) name.textContent = sel[o.index] || '';
    });
  }
  function renderLadder() {
    const tiers = buy.querySelector('[data-ladder]'); if (!tiers) return;
    const rows = ladder(); const cur = currentRow();
    tiers.innerHTML = rows.map((r) => {
      const tag = model.tag(r); const on = cur && r.variant.id === cur.variant.id;
      const full = r.save > 0 ? `<s>${money(Math.round(r.full))}</s>` : '';
      const img = r.variant.image ? `<div class="ph has-img"><img src="${esc(r.variant.image.replace(/width=\d+/, 'width=80'))}" alt="" loading="lazy" width="34" height="34"></div>` : '<div class="ph"></div>';
      return `<label class="pk${tag ? ' has-tag' : ''}${on ? ' sel' : ''}${r.available ? '' : ' unavailable'}" data-qty="${r.qty}" data-price="${r.price}">
        <input type="radio" name="pack-live" value="${r.variant.id}" data-pack data-opt="${model.packIndex}" data-value="${esc(r.variant.options[model.packIndex])}"${on ? ' checked' : ''}>
        ${tag ? `<span class="tag${r.bogo ? ' alt' : ''}">${esc(tag)}</span>` : ''}
        <span class="rad"></span><span class="th">${img}<i>${r.qty}×</i></span>
        <span class="nm">${esc(unitWord(r.qty))}</span>
        <span class="pr">${full}${money(r.price)}</span>
        <span class="meta">${r.available ? model.meta(r) : `<b>${esc(S.soldOut)}</b>`}</span>
      </label>`;
    }).join('');
    if (cur && model.packIndex >= 0) sel[model.packIndex] = cur.variant.options[model.packIndex];
  }
  /* the shipping row of the delivery panel, and the add-on that would make shipping free. The order is the cart plus whatever of this selection
     (the pack, the add-ons switched on) is not in the cart yet: before an add the row reads "the cart with this added",
     after it it reads the cart, so it never disagrees with the drawer. Under the threshold it states the shipping cost
     and what is missing; from the freight limit up it says freight is charged at checkout, as the drawer does. */
  function renderShip() {
    const ship = buy.querySelector('[data-ship]'), v = currentVariant(); if (!ship || !v) return;
    const th = Number(ship.dataset.threshold) || model.freeCents;
    const freight = Number(ship.dataset.freight) || 0;
    const inCart = (ship.dataset.cartIds || '').split(',');
    const adding = [{ id: v.id, price: v.price }].concat(addonsOn()).filter((x) => !inCart.includes(String(x.id))).reduce((a, x) => a + x.price, 0);
    const order = (Number(ship.dataset.cartTotal) || 0) + adding;
    const ok = order >= th, isFreight = freight > 0 && order >= freight;
    const cost = shipCost(order);
    ship.classList.toggle('ok', ok && !isFreight);
    const text = ship.querySelector('[data-ship-text]');
    if (text) text.textContent = isFreight ? S.dlvShipFreight : ok ? S.dlvShipFree : cost > 0 ? fmt(S.dlvShipCost, { cost: money(cost), threshold: moneyShort(th) }) : fmt(S.dlvShipFreeOver, { threshold: moneyShort(th) });
    const togo = ship.querySelector('[data-ship-togo]'), bar = ship.querySelector('[data-ship-bar]');
    const open = !ok && !isFreight;
    if (togo) { togo.hidden = !open; togo.textContent = open ? fmt(S.dlvShipToGo, { amount: money(th - order) }) : ''; }
    if (bar) { bar.hidden = !open; const b = bar.querySelector('i'); if (b) b.style.width = Math.min(100, Math.round(order / th * 100)) + '%'; }
    /* the cheapest add-on, switched off, that takes the order to free shipping carries "Adds free shipping" on its own
       row, beside its switch */
    const one = open ? addonsOff().filter((a) => order + a.price >= th).sort((a, b) => a.price - b.price)[0] : null;
    $$('[data-addon]', buy).forEach((row) => {
      const tag = row.querySelector('[data-adds]'), sw = row.querySelector('[data-addon-id]');
      if (tag) tag.hidden = !(one && sw && Number(sw.dataset.addonId) === one.id);
    });
  }
  /* the line counts the cart, so it is drawn again when the cart changes: an add from this page, a change in the
     drawer, and a return to this page through the back button (the page comes back as it was left) */
  function setCart(c) {
    const ship = buy.querySelector('[data-ship]');
    if (!ship || !c || typeof c.total_price !== 'number') return;
    ship.dataset.cartTotal = c.total_price;
    ship.dataset.cartIds = (c.items || []).map((i) => i.variant_id || i.id).join(',');
    renderShip();
  }
  document.addEventListener('nocry:cart:updated', (e) => setCart(e.detail && e.detail.cart));
  window.addEventListener('pageshow', (e) => { if (e.persisted) fetch(T().routes.cart + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then(setCart).catch(() => {}); });
  function renderPrice() {
    const v = currentVariant(); if (!v) return;
    const q = v.qty, per = Math.round(v.price / q);
    const hp = buy.querySelector('[data-headprice]'), hu = buy.querySelector('[data-headunit]');
    if (hp) hp.textContent = money(v.price);
    const hw = buy.querySelector('[data-headwas]');
    if (hw) { const on = v.compare && v.compare > v.price; hw.hidden = !on; hw.textContent = on ? money(v.compare) : ''; }
    if (hu) hu.innerHTML = q === 1 ? '' : `${esc(unitWord(q))} · <b>${money(per)}</b> ${esc(S.each)}`;
    const addTotal = addonsOn().reduce((a, x) => a + x.price, 0);
    const total = v.price + addTotal;
    const bp = buy.querySelector('[data-btnprice]'); if (bp) bp.textContent = money(total);
    const st = document.querySelector('[data-sticky]');
    if (st) { st.querySelector('[data-qtylabel]').textContent = unitWord(q); st.querySelector('[data-total]').textContent = money(total); }
    const idInput = buy.querySelector('[data-variant-id]'); if (idInput) idInput.value = v.id;
    const inst = buy.querySelector('[data-installments-id]'); if (inst && inst.value !== String(v.id)) { inst.value = v.id; inst.dispatchEvent(new Event('change', { bubbles: true })); }
    const atc = buy.querySelector('[data-atc]'), atcLabel = buy.querySelector('[data-atc-label]');
    if (atc) {
      atc.disabled = !v.available;
      if (atcLabel) { if (!atcLabel.dataset.label) atcLabel.dataset.label = S.addToCart || atcLabel.textContent; atcLabel.textContent = v.available ? atcLabel.dataset.label : S.soldOut; }
    }
    renderShip();
    renderDelivery();
    /* express checkout waits for a complete selection: the wallet buttons read the form's variant id and skip our checks */
    const complete = !missing().length;
    const guard = buy.querySelector('[data-express-guard]'), wallets = buy.querySelector('[data-express-inner]');
    if (guard) guard.hidden = complete;
    if (wallets) wallets.inert = !complete;
    /* URL + events: only for a variant the visitor has fully chosen */
    const exact = complete ? model.find(sel) : null;
    if (exact) {
      const u = new URL(location.href); u.searchParams.set('variant', exact.id); history.replaceState(history.state, '', u);
      dispatchProductSelect({ productId: String(model.d.id), variantId: String(exact.id) }, buy);
    }
  }
  /* ---- the delivery panel: arrival window and ship day ----
     Everything runs on the warehouse clock (theme settings, Commerce: cutoff hour, time zone, transit days at least and
     at most), so the cutoff is right for every visitor. Weekends never ship and see no delivery; nor do the US carrier
     holidays (when switched on) and the days off listed in the settings. Days are held as UTC dates, so day arithmetic
     never crosses a clock change. An order before the cutoff on a working day ships that day, else the next working day;
     it arrives from ship day + transit (at least) to ship day + transit (at most) working days. */
  const DAY = 864e5;
  const cutoffH = Number(SET.cutoffHour) || 14;
  const clampDays = (n, d) => Math.min(30, Math.max(1, Math.round(Number(n) || d)));
  const tMax = clampDays(SET.transitDays, 4);
  const tMin = Math.min(tMax, clampDays(SET.transitDaysMin, tMax));
  const cutoffLabel = SET.cutoffLabel || `${cutoffH % 12 || 12} ${cutoffH >= 12 ? 'pm' : 'am'} ET`;
  const lang = document.documentElement.lang || 'en-US';
  const utc = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
  const key = (x) => x.toISOString().slice(0, 10);
  /* a mistyped time zone in the settings falls back to the warehouse's own (New York), not to the visitor's clock */
  function warehouseNow() {
    let y, m, d, h, mi;
    const read = (tz) => {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }).formatToParts(new Date());
      const get = (t) => Number((parts.find((x) => x.type === t) || {}).value);
      y = get('year'); m = get('month'); d = get('day'); h = get('hour') % 24; mi = get('minute');
    };
    try { read(SET.cutoffTimezone || 'America/New_York'); } catch (e) { try { read('America/New_York'); } catch (e2) { y = NaN; } }
    if (![y, m, d, h, mi].every(Number.isFinite)) { const n = new Date(); y = n.getFullYear(); m = n.getMonth() + 1; d = n.getDate(); h = n.getHours(); mi = n.getMinutes(); }
    return { day: utc(y, m, d), mins: h * 60 + mi, year: y };
  }
  /* US carrier holidays for a year: fixed dates move to Friday or Monday when they fall on a weekend */
  const holidayCache = {};
  function carrierHolidays(y) {
    if (holidayCache[y]) return holidayCache[y];
    const nth = (m, wd, n) => { const x = utc(y, m, 1); x.setUTCDate(1 + ((wd - x.getUTCDay() + 7) % 7) + (n - 1) * 7); return x; };
    const last = (m, wd) => { const x = utc(y, m + 1, 0); x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() - wd + 7) % 7)); return x; };
    const observed = (m, d) => { const x = utc(y, m, d); if (x.getUTCDay() === 6) x.setUTCDate(d - 1); if (x.getUTCDay() === 0) x.setUTCDate(d + 1); return x; };
    holidayCache[y] = new Set([observed(1, 1), last(5, 1), observed(7, 4), nth(9, 1, 1), nth(11, 4, 4), observed(12, 25)].map(key));
    return holidayCache[y];
  }
  /* no delivery: weekends and carrier holidays (New Year's Day on a Saturday is kept on Dec 31, so the next year's list is
     read too). No shipping: those, plus the warehouse's own days off */
  const daysOff = new Set(String(SET.daysOff || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
  const noDelivery = (x) => {
    const wd = x.getUTCDay(); if (wd === 0 || wd === 6) return true;
    if (SET.carrierHolidays === false) return false;
    const k = key(x), y = x.getUTCFullYear();
    return carrierHolidays(y).has(k) || carrierHolidays(y + 1).has(k);
  };
  const offDay = (x) => noDelivery(x) || daysOff.has(key(x));
  const dayAfter = (x, skip) => { const n = new Date(x); do n.setUTCDate(n.getUTCDate() + 1); while (skip(n)); return n; };
  const nextWork = (x) => dayAfter(x, offDay);
  const addWork = (x, k) => { let n = new Date(x); for (let i = 0; i < k; i++) n = dayAfter(n, noDelivery); return n; };
  const dayText = (x, weekday) => x.toLocaleDateString(lang, { weekday: weekday ? 'short' : undefined, month: 'short', day: 'numeric', timeZone: 'UTC' });
  /* "Oct 19 – 21", "Oct 30 – Nov 2", or one date when the two ends meet */
  function rangeText(a, b) {
    if (key(a) === key(b)) return dayText(a);
    const sameMonth = a.getUTCMonth() === b.getUTCMonth();
    return `${dayText(a)} – ${sameMonth ? b.getUTCDate() : dayText(b)}`;
  }
  const strongIn = (str, k, val) => esc(str).split(`[${k}]`).join(`<b>${esc(val)}</b>`);
  function renderDelivery() {
    const panel = buy.querySelector('[data-dlv]'); if (!panel) return;
    const v = currentVariant(); if (!v) return;
    const lead = panel.querySelector('[data-dlv-date]'), stock = panel.querySelector('[data-dlv-stock]'), stockText = panel.querySelector('[data-dlv-stock-text]');
    if (!v.available) {
      if (lead) lead.textContent = S.soldOut;
      if (stock) stock.hidden = true;
      return;
    }
    const now = warehouseNow();
    const sameDay = !offDay(now.day) && now.mins < cutoffH * 60;
    const ship = sameDay ? new Date(now.day) : nextWork(now.day);
    if (lead) lead.textContent = fmt(S.dlvArrives, { date: rangeText(addWork(ship, tMin), addWork(ship, tMax)) });
    /* the stock line keeps the ship time the server printed from the theme settings ("In stock · ships in 1 to 2 business
       days"). It no longer counts down to the cutoff or names a ship day: in Sep to Oct 2026 only 40% of weekday orders
       placed before the cutoff left the warehouse that day, so "order in 3 h to ship today" was a promise the warehouse's
       own times did not support. The arrival range above still starts from the cutoff ship day; measured against real
       deliveries, it holds (96% by its last date). */
    if (stock) stock.hidden = false;
  }
  /* the arrival dates move with the clock (past the cutoff, and at midnight): redrawn every half minute while the page is in
     view, and on return */
  if (buy.querySelector('[data-dlv]')) {
    const tick = () => { if (!buy.isConnected) { clearInterval(timer); document.removeEventListener('visibilitychange', tick); return; } if (!document.hidden) renderDelivery(); };
    const timer = setInterval(tick, 30000);
    document.addEventListener('visibilitychange', tick);
  }
  function render() { renderOptions(); renderLadder(); renderPrice(); filterGallery(); }

  /* ---- events ---- */
  buy.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-opt]');
    if (input) {
      const i = parseInt(input.dataset.opt, 10);
      sel[i] = input.dataset.value || input.value;
      if (i === model.colorIndex) jumpToVariantMedia();
      render(); track('option_selected', { option: model.options[i].name, value: sel[i] });
      return;
    }
  });
  buy.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-addon-id]');
    if (sw) { const on = sw.getAttribute('aria-checked') !== 'true'; sw.setAttribute('aria-checked', String(on)); sw.classList.toggle('on', on); track('addon_toggled', { addon: sw.dataset.addonId, on }); renderPrice(); return; }
    const row = e.target.closest('[data-addon]');
    if (row && !e.target.closest('a')) { const b = row.querySelector('[data-addon-id]'); if (b) b.click(); return; }
    if (e.target.closest('[data-sizeguide]')) { e.preventDefault(); openSizeGuide(); }
    if (e.target.closest('[data-express-guard]')) { e.preventDefault(); promptMissing(); }
  });
  buy.addEventListener('keydown', (e) => {
    const t = e.target.closest('.pk, .chip.opt, .csw'); if (!t) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { const n = t.nextElementSibling; if (n && n.querySelector('input')) { e.preventDefault(); n.querySelector('input').click(); n.querySelector('input').focus(); } }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { const p = t.previousElementSibling; if (p && p.querySelector('input')) { e.preventDefault(); p.querySelector('input').click(); p.querySelector('input').focus(); } }
  });

  const form = buy.querySelector('form[data-product-form]');
  /* a required option is still open: mark its row, bring it into view, and say so */
  function promptMissing() {
    const miss = missing(); if (!miss.length) return false;
    const row = buy.querySelector(`[data-optrow="${miss[0].index}"]`);
    if (row) { row.classList.add('err'); row.scrollIntoView({ block: 'center', behavior: 'smooth' }); const first = row.querySelector('input'); if (first) first.focus({ preventScroll: true }); }
    return true;
  }
  async function addToCart(source = 'pdp') {
    if (promptMissing()) return false;
    const v = model.find(sel); if (!v) return false;
    if (!v.available) return false;
    const btn = buy.querySelector('[data-atc]'); btn.setAttribute('aria-busy', 'true');
    const items = [{ id: v.id, quantity: 1 }].concat(addonsOn().map((a) => ({ id: a.id, quantity: 1 })));
    const result = await cart.add(items, { source, destination: 'pdp', context: 'product', target: buy });
    btn.removeAttribute('aria-busy');
    const err = buy.querySelector('[data-form-error]'); if (err) { err.classList.toggle('show', !result); err.textContent = result ? '' : S.cartError; }
    return !!result;
  }
  if (form) form.addEventListener('submit', (e) => { e.preventDefault(); addToCart('pdp'); });

  /* ---- gallery ---- */
  const gallery = root.querySelector('[data-gallery]');
  /* the slides in play: another colour's photos (data-off, below) are out of the arrows, the swipe and the lightbox */
  const slides = () => $$('[data-gmain] [data-slide]', root).filter((s) => !s.hasAttribute('data-off'));
  function showSlide(id) {
    slides().forEach((sl) => { sl.hidden = sl.dataset.slide !== String(id); });
    $$('[data-thumb]', root).forEach((t) => { const on = t.dataset.thumb === String(id); t.classList.toggle('on', on); t.setAttribute('aria-selected', String(on)); });
    if (window.innerWidth < 900) { const sl = slides().find((x) => x.dataset.slide === String(id)); const main = root.querySelector('[data-gmain]'); if (sl && main) { const pad = parseFloat(getComputedStyle(main).paddingLeft) || 0; main.scrollTo({ left: sl.offsetLeft - main.offsetLeft - pad, behavior: 'smooth' }); } }
  }
  /* colour sets: snippets/product-gallery.liquid tags each colour's photos with data-color; only the chosen colour's
     set shows, with the photos every colour shares. A colour change, from the swatches or the quick-add sheet, opens on
     that colour's photo (its set's first). A colour whose set is its variant photo alone shows every photo. The video keeps
     its place among the photos that show (data-video-before photos ahead of it), as the snippet places it. */
  let shownColor;
  function placeVideos() {
    const before = parseInt(gallery.dataset.videoBefore, 10) || 3;
    [['[data-gthumbs]', '[data-thumb]', (el) => el.hasAttribute('data-vid')], ['[data-gmain]', '[data-slide]', (el) => el.classList.contains('isvideo')]].forEach(([box, item, isVid]) => {
      const wrap = gallery.querySelector(box); if (!wrap) return;
      const items = $$(item, wrap), vids = items.filter(isVid); if (!vids.length) return;
      const photos = items.filter((el) => !isVid(el) && !el.hasAttribute('data-off')); if (!photos.length) return;
      const anchor = photos.length > before ? photos[before - 1] : photos[photos.length - 1];
      anchor.after(...vids);
    });
  }
  function filterGallery() {
    if (model.colorIndex < 0) return;
    const tagged = $$('[data-gallery] [data-color]', root); if (!tagged.length) return;
    const c = sel[model.colorIndex]; if (c == null || c === shownColor) return;
    const firstRun = shownColor === undefined; shownColor = c;
    const mine = tagged.filter((el) => el.dataset.color === c && el.hasAttribute('data-slide'));
    const all = mine.length < 2;
    tagged.forEach((el) => {
      const off = !all && el.dataset.color !== c;
      if (off) el.setAttribute('data-off', ''); else el.removeAttribute('data-off');
      if (off && el.hasAttribute('data-slide')) el.hidden = true;
    });
    if (gallery) placeVideos();
    const cur = slides().find((x) => !x.hidden);
    if (!firstRun || !cur) { const go = mine[0] || slides()[0]; if (go) showSlide(go.dataset.slide); }
  }
  function jumpToVariantMedia() { const v = model.find(sel) || currentVariant(); if (v && v.mediaId && slides().some((s) => s.dataset.slide === String(v.mediaId))) showSlide(v.mediaId); }
  if (gallery) {
    gallery.addEventListener('click', (e) => { const t = e.target.closest('[data-thumb]'); if (t) showSlide(t.dataset.thumb); });
    /* desktop shows one slide at a time: the arrows, a swipe on a touch screen and the arrow keys step through them in the
       thumbnails' order, round from the last to the first (on phones the slides are a swipe row of their own) */
    const at = () => Math.max(0, slides().findIndex((s) => !s.hidden));
    const step = (d) => { const l = slides(); if (l.length < 2) return; showSlide(l[(at() + d + l.length) % l.length].dataset.slide); };
    gallery.addEventListener('click', (e) => { if (e.target.closest('[data-gprev]')) step(-1); else if (e.target.closest('[data-gnext]')) step(1); });
    gallery.addEventListener('keydown', (e) => {
      if (window.innerWidth < 900 || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.target.tagName === 'VIDEO') return;
      e.preventDefault(); step(e.key === 'ArrowRight' ? 1 : -1);
      if (e.target.closest('[data-thumb]')) { const on = gallery.querySelector('[data-thumb].on'); if (on) on.focus(); }
    });
    const gm = gallery.querySelector('[data-gmain]');
    if (gm) {
      let tx = null, ty = 0;
      gm.addEventListener('touchstart', (e) => { if (window.innerWidth < 900 || e.touches.length > 1) { tx = null; return; } tx = e.touches[0].clientX; ty = e.touches[0].clientY; }, { passive: true });
      gm.addEventListener('touchend', (e) => {
        if (tx === null) return; const dx = e.changedTouches[0].clientX - tx, dy = e.changedTouches[0].clientY - ty; tx = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      }, { passive: true });
    }
    /* a YouTube or Vimeo link among the media opens the video lightbox (the page-wide opener leaves the gallery to this script) */
    const embed = (e) => e.target.closest && e.target.closest('[data-gmain] [data-slide][data-video-embed]');
    gallery.addEventListener('click', (e) => { const v = embed(e); if (v && window.NOCRY && window.NOCRY.video) { e.preventDefault(); window.NOCRY.video.open(v); } });
    gallery.addEventListener('keydown', (e) => { const v = embed(e); if (v && (e.key === 'Enter' || e.key === ' ') && window.NOCRY && window.NOCRY.video) { e.preventDefault(); window.NOCRY.video.open(v); } });
    /* dots on phones */
    const main = root.querySelector('[data-gmain]'), dots = root.querySelector('.gdots');
    if (main && dots) {
      const n = slides().length; dots.innerHTML = ''; for (let i = 0; i < n; i++) { const d = document.createElement('i'); if (i === 0) d.className = 'on'; dots.appendChild(d); }
      main.addEventListener('scroll', debounce(() => { const w = (slides()[0] && slides()[0].getBoundingClientRect().width + 10) || 1; const k = Math.min(n - 1, Math.round(main.scrollLeft / w)); Array.from(dots.children).forEach((x, j) => { x.className = j === k ? 'on' : ''; }); }, 60), { passive: true });
    }
    /* lightbox */
    const box = document.querySelector('[data-imgbox]');
    if (box) {
      const stage = box.querySelector('[data-imgstage]'), count = box.querySelector('[data-imgcount]');
      const list = () => slides().filter((s) => !s.classList.contains('isvideo') && s.querySelector('img'));
      let idx = 0;
      const show = (i) => { const l = list(); if (!l.length) return; idx = (i + l.length) % l.length; const img = l[idx].querySelector('img'); stage.innerHTML = `<div class="ph has-img"><img src="${img.currentSrc || img.src}" srcset="${img.getAttribute('srcset') || ''}" sizes="100vw" alt="${esc(img.alt)}"></div>`; count.textContent = fmt(S.photoOf, { i: idx + 1, n: l.length }); };
      const open = (i) => { show(i); box.hidden = false; layers.open('img', box); track('gallery_zoom', { index: idx }); };
      const close = () => { if (box.hidden) return; box.hidden = true; layers.close('img'); };
      root.addEventListener('click', (e) => {
        const sl = e.target.closest('[data-gmain] [data-slide]:not(.isvideo)');
        if (sl) { const l = list(); open(Math.max(0, l.indexOf(sl))); }
      });
      root.addEventListener('keydown', (e) => { const sl = e.target.closest && e.target.closest('[data-gmain] [data-slide]:not(.isvideo)'); if (sl && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(Math.max(0, list().indexOf(sl))); } });
      box.querySelector('[data-imgclose]').addEventListener('click', close);
      box.querySelector('[data-imgprev]').addEventListener('click', () => show(idx - 1));
      box.querySelector('[data-imgnext]').addEventListener('click', () => show(idx + 1));
      box.addEventListener('click', (e) => { if (e.target === box) close(); });
      document.addEventListener('keydown', (e) => { if (box.hidden) return; if (e.key === 'ArrowRight') show(idx + 1); if (e.key === 'ArrowLeft') show(idx - 1); });
      document.addEventListener('nocry:layer:close', (e) => { if (e.detail.name === 'img') box.hidden = true; });
      let sx = 0; stage.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => { const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) show(idx + (dx < 0 ? 1 : -1)); }, { passive: true });
    }
  }

  /* ---- size guide ---- */
  const sg = document.querySelector('[data-sizeguide-modal]');
  function openSizeGuide() {
    if (!sg) return;
    const picks = sg.querySelector('[data-sizeguide-picks]'); const sizeOpt = model.sizeOptions[0];
    if (picks && sizeOpt) picks.innerHTML = sizeOpt.values.map((v, i) => `<button class="btn ${i === 0 ? 'btn-dark' : 'btn-line'} btn-sm" type="button" data-pickfit="${esc(v)}">${esc(fmt(S.chooseX, { value: v }))}</button>`).join('');
    sgOpener = document.activeElement;
    sg.classList.add('open'); focusInto(sg, sg.querySelector('[data-sizeclose]')); track('size_guide_opened', {});
  }
  let sgOpener = null;
  function closeSizeGuide() {
    if (!sg.classList.contains('open')) return;
    sg.classList.remove('open');
    if (keyboardUser() && sgOpener && sgOpener.focus) sgOpener.focus({ preventScroll: true });
    else if (sg.contains(document.activeElement)) document.activeElement.blur();
  }
  if (sg) {
    sg.addEventListener('click', (e) => {
      if (e.target === sg || e.target.closest('[data-sizeclose]')) closeSizeGuide();
      const pf = e.target.closest('[data-pickfit]'); if (pf) { const o = model.sizeOptions[0]; if (o) { sel[o.index] = pf.dataset.pickfit; render(); } closeSizeGuide(); }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSizeGuide(); });
    document.addEventListener('click', (e) => { if (e.target.closest('[data-sizeguide]') && !buy.contains(e.target)) { e.preventDefault(); openSizeGuide(); } });
  }

  /* ---- sticky bar: shown once the buy button scrolls out ---- */
  const sticky = document.querySelector('[data-sticky]'), atcBtn = buy.querySelector('[data-atc]');
  if (sticky && atcBtn && 'IntersectionObserver' in window) {
    sticky.hidden = false;
    new IntersectionObserver((es) => { sticky.style.display = es[0].isIntersecting ? 'none' : ''; }, { threshold: 0.2 }).observe(atcBtn);
  }

  /* ---- public API for the sheet and the lander ---- */
  window.NOCRY = Object.assign(window.NOCRY || {}, { pdp: { model, getSelection: () => sel.slice(), setSelection: (s) => { sel = s.slice(); render(); }, addToCart, render } });

  render();
  if (fromUrl && !packOnly) jumpToVariantMedia();
  track('pdp_view', { product: model.d.handle, variant: currentVariant() ? currentVariant().id : '', pack: qty() + 'PACK', utm_content: getParam('utm_content') || '' });
}

onReady(() => $$('[data-pdp]').forEach(initPDP));
document.addEventListener('shopify:section:load', (e) => $$('[data-pdp]', e.target).forEach(initPDP));
